import { AqdbBatch } from '../../../../aqdb/models/aqdb-batch';
import { TranslationService } from '../../../services/translation.service';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import { CommonColDef, MultiLineColDef } from '../../column-defs';
import { ColumnType } from '../../column-type';

export class AqdbMechanicalCharacteristicsColumnDefinition {
	readonly AQDB_MECHANICAL_CHARACTERISTICS_CONFIGURATION:
	Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>;

	constructor(private readonly translationService: TranslationService) {
		this.AQDB_MECHANICAL_CHARACTERISTICS_CONFIGURATION =
		new Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>(
			[
				[
					'mechanicalCharacteristicsTensile',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.mechanical.characteristics.tensile.test'),
						colId: 'mechanicalCharacteristicsTensile',
						hide: true,
						sortable: false,
					})
				],
				[
					'tensileTestNumber',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.test.number.tensile'),
						fields: ['segments.tensileList.testNumber'],
						colId: 'tensileTestNumber',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.testNumber).join(';') : '';
						},
					})
				],
				[
					'sampleDirection',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.tensile.direction'),
						fields: ['segments.tensileList.sampleDirectionCode'],
						colId: 'sampleDirection',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => typeof tensile.sampleDirectionCode === 'string'
										? tensile.sampleDirectionCode.trim() === 'T'
											? this.translationService.getTR('aqdb.direction.T')
											: tensile.sampleDirectionCode.trim() === 'L'
												? this.translationService.getTR('aqdb.direction.L') : ''
										: '').join(';') : '';
						},
					})
				],
				[
					'valueYs',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.yield.stress'),
						fields: ['segments.tensileList.valueYs'],
						colId: 'valueYs',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						cellRendererParams: { numericFormat: 'number' },
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.valueYs).join(';') : '';
						},
					})
				],
				[
					'unitYs',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.unit'),
						fields: ['segments.tensileList.unitYs'],
						colId: 'unitYs',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.unitYs).join(';') : '';
						},
					})
				],
				[
					'ultimateTensileStrength',
					new MultiLineColDef<AqdbBatch>({
						autoHeight: true,
						headerName: this.translationService.getTR('aqdb.tensile.ultimate.tensile.stress'),
						fields: ['segments.tensileList.ultimateTensileStrength'],
						colId: 'ultimateTensileStrength',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.ultimateTensileStrength).join(';') : '';
						},
					})
				],
				[
					'unitUTS',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.unit'),
						fields: ['segments.tensileList.unitUTS'],
						colId: 'unitUTS',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.unitUTS).join(';') : '';
						},
					})
				],
				[
					'elongation',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.tensile.total.lengthening'),
						fields: ['segments.tensileList.elongation'],
						colId: 'elongation',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.elongation).join(';') : '';
						},
					})
				],
				[
					'strainHardeningRatio',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.tensile.work.hardening.coefficient'),
						fields: ['segments.tensileList.strainHardeningRatio'],
						colId: 'strainHardeningRatio',
						groupId: 'mechanicalCharacteristicsTensile',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.tensileList)
									.map(tensile => tensile.strainHardeningRatio).join(';') : '';
						},
					})
				],
				[
					'mechanicalCharacteristicsAnisotropy',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.mechanical.characteristics.anisotropy'),
						colId: 'mechanicalCharacteristicsAnisotropy',
						hide: true,
						sortable: false,
					})
				],
				[
					'anisotropyCoefficient',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.anisotropy.r'),
						fields: ['segments.anisotropyList.anisotropyCoefficient'],
						colId: 'anisotropyCoefficient',
						groupId: 'mechanicalCharacteristicsAnisotropy',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.anisotropyList)
									.map(anisotropy => anisotropy.anisotropyCoefficient).join(';') : '';
						},
					})
				],
			]
		);
	}
}
